/**
 * CollectionsHomeTab - Home tab showing Collections as cards
 *
 * Atomic level: Organism
 * Displays collections in a card grid with search and import functionality
 *
 * Phase 2 of Collection-First Conduct Records redesign:
 * - Removed table view in favor of card grid
 * - Removed ViewsSection (views are now collection-scoped in detail page)
 * - Cards sorted by conduct score (highest first, N/A at end)
 *
 * Following GO UI Design System patterns
 */

import React, { useState, useMemo } from 'react';
import { Plus, Search, Upload } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { CollectionCardGrid } from './components/CollectionCardGrid';
import { useCollections } from '../../../hooks/useRecords';
import { useAuth } from '../../../contexts/AuthContext';
import { isAdmin } from '../../../utils/rbac';
import type { Collection } from '../../../services/types';
import SignalCreationHub from '../../v3/signals/SignalCreationHub';
import { toast } from 'sonner';

export const CollectionsHomeTab: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [isImportOpen, setIsImportOpen] = useState(false);

  // Fetch collections
  const { data: collectionsData, isLoading } = useCollections();

  const collectionTypeSummary = useMemo(() => {
    const counts = new Map<string, number>();

    for (const collection of collectionsData?.collections || []) {
      const type = collection.type?.trim() || 'unknown';
      counts.set(type, (counts.get(type) || 0) + 1);
    }

    return Array.from(counts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([type, count]) => ({ type, count }));
  }, [collectionsData?.collections]);

  // Filter collections by search query
  const filteredCollections = useMemo(() => {
    const collections = collectionsData?.collections || [];
    if (!searchQuery.trim()) return collections;
    const query = searchQuery.toLowerCase();
    return collections.filter((collection) =>
      collection.name.toLowerCase().includes(query)
    );
  }, [collectionsData?.collections, searchQuery]);

  const handleCollectionClick = (collection: Collection) => {
    navigate(`/v4/records/${collection.id}`);
  };

  return (
    <>
      <div className="space-y-6">
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {collectionTypeSummary.map((item) => (
              <Badge
                key={item.type}
                variant="outline"
                className="rounded-full border-border/70 bg-background px-3 py-1 text-[11px] font-semibold capitalize text-foreground shadow-none"
              >
                {item.type}
                <span className="ml-1 text-muted-foreground">({item.count})</span>
              </Badge>
            ))}
            {isAdmin(user) && (
              <Button asChild variant="outline" className="gap-2">
                <Link to="/v4/monitor/new">
                  <Plus className="h-4 w-4" />
                  New monitor
                </Link>
              </Button>
            )}
            <Button
              onClick={() => setIsImportOpen(true)}
              className="gap-2"
            >
              <Upload className="h-4 w-4" />
              Import records
            </Button>
          </div>

          <div className="relative max-w-2xl">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search collections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 bg-background pl-9"
            />
          </div>
        </div>

        {/* Collection cards grid */}
        <CollectionCardGrid
          collections={filteredCollections}
          onCollectionClick={handleCollectionClick}
          isLoading={isLoading}
        />

        {/* Empty search state */}
        {!isLoading && filteredCollections.length === 0 && searchQuery && (
          <div className="rounded-2xl border border-border/70 bg-background px-6 py-10 text-center shadow-[var(--shadow-card)]">
            <p className="text-sm text-muted-foreground">
              No collections match "{searchQuery}"
            </p>
          </div>
        )}
      </div>

      {/* Import Modal */}
      <SignalCreationHub
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onSignalCreated={() => {
          setIsImportOpen(false);
          toast.success('Record created successfully');
        }}
        onImportComplete={() => {
          setIsImportOpen(false);
          toast.success('Records imported successfully');
        }}
      />
    </>
  );
};
